import { Tag, Divider, Button } from 'antd';
import { DownloadOutlined, BellOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { createInvoiceShowEditActions } from '../../../utils/createInvoiceShowEditActions';
import { InvoiceStatus } from '../../../utils/constants/tagColors';
import { useListResourceArchiving } from '../../../utils/listResourceArchiving';
import { statusSorter } from '../../../utils/sorterMethods';
import { TemplateDocumentButton } from '../../../components/TemplateDocumentButton/TemplateDocumentButton';
import { formatInvoiceAmount } from './utils/formatInvoiceAmount';
import { invoicesFileName } from './utils/invoicesFileName';
import { templateUseCase } from './utils/templateUseCase';

// Une facture offerte ne se télécharge que pour la cotisation, seule à avoir
// un modèle de document FREE
const isDownloadable = ({ status, type }) =>
  ['PAID', 'WAITING'].includes(status) ||
  (status === 'FREE' && type === 'SUBSCRIPTION');

/**
 * Fonction utilitaire qui génère les colonnes pour la liste des factures client.
 * @function
 * @param {Object} options - Les options de configuration.
 * @param {Object} options.enums - Les énumérations pour les statuts et types de facture.
 * @param {boolean} options.refreshData - L'état pour forcer le rafraîchissement des données.
 * @param {Function} options.setRefreshData - Fonction pour définir l'état de rafraîchissement des données.
 * @param {Function} options.setRecipient - Fonction pour définir le destinataire de la facture.
 * @param {Function} options.setIsReminderModalOpen - Fonction pour définir l'ouverture du modal de rappel.
 * @returns {Array} Un tableau contenant les colonnes pour la liste des factures client.
 */

export const useColumns = ({
  enums,
  setRefreshData,
  setRecipient,
  setIsReminderModalOpen,
  setCustomerInvoice,
  setDocumentType,
  setRecordID
}) => {
  const { t } = useTranslation();

  const { deleteResource, restoreResource } = useListResourceArchiving(
    setRefreshData,
    'customerinvoices/archiving'
  );

  const columns = [
    {
      title: t('customerinvoices.form.reference'),
      key: 'reference',
      dataIndex: 'reference',
      sorter: true
    },
    {
      title: t('customerinvoices.form.type'),
      key: 'type',
      dataIndex: ['type'],
      render: (type) =>
        type ? (
          <Tag color={InvoiceStatus[type]}>
            {t(`customerinvoices.tags.${type}`, {
              defaultValue: type
            })}
          </Tag>
        ) : (
          'N/R'
        ),
      sorter: (a, b) => {
        const typeA = t(`customerinvoices.tags.${a?.type}`, {
          defaultValue: a?.type
        });
        const typeB = t(`customerinvoices.tags.${b?.type}`, {
          defaultValue: b?.type
        });

        return typeA.localeCompare(typeB);
      }
    },
    {
      title: t('customerinvoices.form.customer'),
      key: 'customer.last_name',
      dataIndex: 'customer',
      render: (customer) =>
        customer &&
        `${customer?.last_name || ''} ${customer?.first_name || ''}`,
      sorter: true
    },
    {
      title: t('customerinvoices.form.customer_reference'),
      key: 'customer.reference',
      dataIndex: ['customer', 'reference'],
      sorter: true
    },
    {
      title: t('customerinvoices.form.supervision_reference'),
      key: 'supervision.reference',
      dataIndex: ['supervision', 'reference'],
      sorter: true
    },
    {
      title: t('customerinvoices.form.due_date'),
      key: 'due_date',
      dataIndex: 'due_date',
      sorter: true,
      render: (due_date) => due_date && dayjs(due_date).format('DD/MM/YYYY')
    },
    {
      title: t('customerinvoices.form.total_ttc'),
      key: 'total_ttc',
      render: (record) =>
        formatInvoiceAmount(record?.total_ttc, record?.franc ? 'Fr' : '€'),
      sorter: true
    },
    {
      title: t('customerinvoices.form.balance'),
      key: 'balance',
      dataIndex: 'balance',
      render: (balance, record) =>
        formatInvoiceAmount(balance, record?.franc ? 'Fr' : '€'),
      sorter: true
    },
    {
      title: t('customerinvoices.form.status'),
      key: 'status',
      dataIndex: ['status'],
      render: (status) =>
        status ? (
          <Tag color={InvoiceStatus[status]}>
            {t(`customerinvoices.tags.${status}`)}
          </Tag>
        ) : (
          'N/R'
        ),
      sorter: (a, b) => statusSorter(a, b, 'customerinvoices.tags', t)
    },
    {
      key: 'action',
      align: 'right',
      fixed: 'right',
      width: 120,
      render: (record) => (
        <>
          {enums.status && ['UNPAID'].includes(record.status) && (
            <>
              <Button
                type="link"
                onClick={() => {
                  const documentType =
                    record.type === 'SUBSCRIPTION'
                      ? 'SUBSCRIPTION-INVOICE'
                      : 'OWNER-INVOICE';
                  setRecipient(record.customer._id);
                  setIsReminderModalOpen(true);
                  setCustomerInvoice(record);
                  setDocumentType(documentType);
                  setRecordID(record._id);
                }}
                style={{ padding: 0 }}
              >
                <BellOutlined style={{ fontSize: 18 }} />
              </Button>
              <Divider type="vertical" />
            </>
          )}
          {createInvoiceShowEditActions({
            t,
            enums,
            record,
            deleteResource,
            restoreResource,
            showPath: '/invoices/customers/show',
            editPath: '/invoices/customers/edit'
          })}
          {enums.status && isDownloadable(record) && (
            <>
              <Divider type="vertical" />
              <TemplateDocumentButton
                parameters={[
                  {
                    collection: 'Customerinvoice',
                    documentId: record?._id
                  }
                ]}
                use_case={templateUseCase(record)}
                extraQuery="type=TEMPLATE_DOCS"
                button_text="customerinvoices.show.buttons.download"
                downloadName={invoicesFileName(record, t)}
                icon={<DownloadOutlined style={{ fontSize: 18 }} />}
                withIcon={false}
              />
            </>
          )}
        </>
      )
    }
  ];

  const headers = [
    {
      label: 'reference',
      key: 'reference'
    },
    {
      label: 'type',
      key: 'customerinvoice_type'
    },
    {
      label: 'customer',
      key: 'customer'
    },
    {
      label: 'customer_reference',
      key: 'customer_reference'
    },
    {
      label: 'supervision_reference',
      key: 'supervision_reference'
    },
    {
      label: 'due_date',
      key: 'due_date'
    },
    {
      label: 'total_ttc',
      key: 'total_ttc'
    },
    {
      label: 'balance',
      key: 'balance'
    },
    {
      label: 'status',
      key: 'customerinvoice_status'
    }
  ];

  return { columns, headers };
};
